import { Segment } from '@dna/analytics'
import { getSectionPosition, viewportObserver } from '@dna/utils'

if (!customElements.get('dna-editorial-multiproduct-carousel')) {
  class DNAEditorialMultiProductCarousel extends HTMLElement {
    constructor() {
      super()

      this.empcDirection = false
      this.empcPosition = 1
      this.experimentationTrackingKey = this.getAttribute(
        'experimentation-tracking-key',
      )
      this.trackingKey = this.getAttribute('tracking-key')
    }

    connectedCallback() {
      this.$section = this.closest('.shopify-section')
      this.sectionPosition = getSectionPosition(this.$section)
      this.$empcBanner = this.querySelector('.js-empc-banner')
      this.$empcHeadings = this.querySelectorAll('.js-empc-heading')
      this.$empcCarousel = this.querySelector('.js-empc-content sl-carousel')
      this.$empcNavButtons = this.querySelectorAll('.js-empc-navigation button')
      this.$empcCtas = this.querySelectorAll('.js-empc-cta')
      this.$empcProducts = this.querySelectorAll('.js-empc-product-link')

      this.empcHeadings = this.getAttribute('empc-headings').split(';')
      this.empcCTAs = this.getAttribute('empc-ctas').split(';')
      this.empcCTALinks = this.getAttribute('empc-cta-links').split(';')

      if (this.experimentationTrackingKey?.length) {
        this.$section.setAttribute(
          'data-experiment-selector',
          this.experimentationTrackingKey,
        )
      }

      // Always start carousel on the first slide
      customElements.whenDefined('sl-carousel').then(() => {
        this.$empcCarousel?.goToSlide(0)
        setTimeout(() => {
          this.$empcCarousel?.addEventListener('sl-slide-change', (event) => {
            this.handleSlideChange(event, this.empcDirection)
          })
        }, 500)
      })

      if (this.$empcNavButtons.length) {
        for (const $button of this.$empcNavButtons) {
          $button.addEventListener('click', (event) => {
            this.handleSlideChangeClick(event)
          })
        }
      }

      if (this.$empcCtas.length) {
        for (const $cta of this.$empcCtas) {
          $cta.addEventListener('click', (event) => {
            this.trackEMPCLinkClick(event)
          })
        }
      }

      if (this.$empcProducts.length) {
        for (const $product of this.$empcProducts) {
          $product.addEventListener('click', (event) => {
            this.trackEMPCProductClick(event)
          })
        }
      }

      if (window.innerWidth > 768) {
        if (this.$empcBanner.hasAttribute('data-animation')) {
          this.$empcBanner.setAttribute('data-animation', 'wipeInDown')
        }
      }
    }

    handleSlideChangeClick(clickEvent) {
      const $button = clickEvent.target.closest('button')
      if ($button?.classList.contains('js-arrow-next')) {
        this.empcDirection = 'next'
        this.$empcCarousel.next()
      } else {
        this.empcDirection = 'previous'
        this.$empcCarousel.previous()
      }
    }

    handleSlideChange(slideEvent, slideDirection) {
      this.empcPosition0 = slideEvent.detail.index
      this.empcPosition = this.empcPosition0 + 1

      // Update banner
      const $empcBannerImages = this.$empcBanner?.querySelectorAll(
        '.empc-banner__image-container',
      )
      if ($empcBannerImages.length) {
        for (const image of $empcBannerImages) {
          const loopIndex = Number(image.getAttribute('data-slide'))
          if (loopIndex === this.empcPosition0) {
            image.classList.add('active')
          } else {
            image.classList.remove('active')
          }
        }
      }

      // Update heading
      let activeHeading = ''
      if (this.empcHeadings[this.empcPosition0]) {
        activeHeading = this.empcHeadings[this.empcPosition0]
      }
      for (const $heading of this.$empcHeadings) {
        $heading.style.opacity = 0.2
        setTimeout(() => {
          $heading.innerHTML = activeHeading.trim()
          $heading.style.opacity = 1
        }, 300)
      }

      // Update content
      const $empcCarouselGrid = this.$empcCarousel?.querySelector(
        'sl-carousel-item.--is-active .js-empc-content-carousel-item-grid',
      )
      if ($empcCarouselGrid && viewportObserver.currentViewport === 'mobile') {
        $empcCarouselGrid.scrollLeft = 0
      }

      // Update CTA
      let activeCTA = ''
      let activeCTALink = ''
      if (this.empcCTAs[this.empcPosition0]) {
        activeCTA = this.empcCTAs[this.empcPosition0]
      }
      if (this.empcCTALinks[this.empcPosition0]) {
        activeCTALink = this.empcCTALinks[this.empcPosition0]
      }
      for (const $cta of this.$empcCtas) {
        $cta.style.opacity = 0.2
        setTimeout(() => {
          $cta.textContent = activeCTA.trim()
          $cta.href = activeCTALink
          $cta.style.opacity = 1
        }, 300)
      }

      setTimeout(() => {
        this.trackEMPCScroll(slideEvent)
      }, 500)
    }

    trackEMPCScroll(event) {
      // @segment
      Segment.protect(() => {
        const $blockTitle = event.target
          .closest('.editorial-multiproduct-carousel')
          ?.querySelector('.js-empc-heading')
        const blockTitle = $blockTitle?.textContent?.trim()

        Segment.track(Segment.CONSTANTS.EVENTS.EMPC_SCROLLED, {
          empc_block_title: blockTitle,
          empc_scroll_direction: this.empcDirection,
          empc_carousel_position: this.empcPosition,
          section_page_position: this.sectionPosition,
          section_tracking_key: this.trackingKey,
        })
      })
    }

    trackEMPCLinkClick(event) {
      // @segment
      Segment.protect(() => {
        const $blockTitle = event.target
          .closest('.editorial-multiproduct-carousel')
          ?.querySelector('.js-empc-heading')
        const blockTitle = $blockTitle?.textContent?.trim()
        const $clickedButton = event.target.closest('.js-empc-cta')
        const $clickedLink = $clickedButton?.renderRoot.querySelector('a')

        Segment.track(Segment.CONSTANTS.EVENTS.EMPC_LINK_CLICKED, {
          empc_block_title: blockTitle,
          empc_cta_text: $clickedButton?.textContent.trim(),
          empc_cta_link: $clickedLink?.getAttribute('href'),
          empc_carousel_position: this.empcPosition,
          section_page_position: this.sectionPosition,
          section_tracking_key: this.trackingKey,
        })
      })
    }

    trackEMPCProductClick(event) {
      // @segment
      Segment.protect(() => {
        const $blockTitle = event.target
          .closest('.editorial-multiproduct-carousel')
          ?.querySelector('.js-empc-heading')
        const blockTitle = $blockTitle?.textContent?.trim()
        const $clickedItem = event.target.closest('.js-empc-product-link')

        Segment.track(Segment.CONSTANTS.EVENTS.EMPC_PRODUCT_CLICKED, {
          empc_block_title: blockTitle,
          empc_clicked_product: $clickedItem?.getAttribute('data-product'),
          empc_clicked_product_position: Number(
            $clickedItem?.getAttribute('data-index'),
          ),
          empc_carousel_position: this.empcPosition,
          section_page_position: this.sectionPosition,
          section_tracking_key: this.trackingKey,
        })
      })
    }
  }
  customElements.define(
    'dna-editorial-multiproduct-carousel',
    DNAEditorialMultiProductCarousel,
  )
}
